<template>
  <div class="file-preview">
    <div v-if="resolvedType === 'image'" class="file-preview-image">
      <img :src="httpsUrl"/>
    </div>
    <video v-else-if="resolvedType === 'video'" :src="httpsUrl" controls class="file-preview-media"></video>
    <audio v-else-if="resolvedType === 'audio'" :src="httpsUrl" controls class="file-preview-audio"></audio>
    <div v-else-if="resolvedType === 'office'" class="handbook-content">
      <iframe :src="officeViewerUrl" width="100%" height="600" frameborder="0"></iframe>
    </div>
    <div v-else-if="resolvedType === 'pdf'" class="handbook-content">
      <iframe :src="httpsUrl" width="100%" height="600" frameborder="0"></iframe>
    </div>
    <div v-else-if="resolvedType === 'csv'" class="csv-preview">
      <div v-if="tableLoading" class="file-preview-unknown"><p>Loading preview...</p></div>
      <div v-else-if="tableError" class="file-preview-unknown">
        <p>{{ tableError }}</p>
        <Button type="primary" @click="download">Download</Button>
      </div>
      <div v-else-if="csvHeaders.length" class="csv-table-wrap">
        <table class="csv-table">
          <thead>
            <tr>
              <th v-for="(h, i) in csvHeaders" :key="'h-' + i">{{ h }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, ri) in csvRows" :key="'r-' + ri">
              <td v-for="(cell, ci) in row" :key="'c-' + ri + '-' + ci">{{ cell }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="file-preview-unknown">
        <p>Preview is empty.</p>
        <Button type="primary" @click="download">Download</Button>
      </div>
    </div>
    <div v-else class="file-preview-unknown">
      <p>This file type cannot be previewed in the browser.</p>
      <Button type="primary" @click="download">Download</Button>
    </div>
  </div>
</template>
<script>
  var PREVIEWABLE = ['image', 'video', 'audio', 'pdf', 'office', 'csv']
  export default {
    name: 'FilePreview',
    props: {
      fileUrl: {type: String, default: ''},
      type: {type: String, default: ''},
      fileName: {type: String, default: ''},
      previewApi: {type: String, default: '/tchLearningMaterial/previewTable'}
    },
    data () {
      return {
        csvHeaders: [],
        csvRows: [],
        tableLoading: false,
        tableError: ''
      }
    },
    computed: {
      httpsUrl () {
        return this.toHttps(this.fileUrl)
      },
      resolvedType () {
        // Prefer the actual file URL (list fileUrl) over fileName: name can be csv while url is pptx.
        let fromUrl = this.guessType(this.httpsUrl || this.fileUrl)
        if (PREVIEWABLE.indexOf(fromUrl) >= 0) return fromUrl
        let fromName = this.guessType(this.fileName)
        if (PREVIEWABLE.indexOf(fromName) >= 0) return fromName
        let t = (this.type || '').toLowerCase()
        if (t === 'text/csv' || t === 'xls' || t === 'xlsx') return 'csv'
        if (t === 'doc' || t === 'docx' || t === 'ppt' || t === 'pptx') return 'office'
        if (PREVIEWABLE.indexOf(t) >= 0) return t
        return fromUrl || fromName || t
      },
      officeViewerUrl () {
        // officeapps src = list fileUrl only (http→https). No fileProxy, no origin concat.
        return 'https://view.officeapps.live.com/op/view.aspx?src=' + encodeURIComponent(this.httpsUrl || '')
      }
    },
    watch: {
      fileUrl: {immediate: true, handler () { this.loadCsvTable() }},
      resolvedType () { this.loadCsvTable() }
    },
    methods: {
      toHttps (url) {
        if (!url) return url
        let s = String(url).trim()
        if (!s) return url
        // Keep existing host. Only rewrite protocol. Never map tutor.kouyujie.cn / OSS onto Qiniu.
        if (/^https:\/\//i.test(s)) return s
        if (/^http:\/\//i.test(s)) return 'https://' + s.substring(7)
        if (s.indexOf('//') === 0) return 'https:' + s
        // host without scheme, e.g. tutor.kouyujie.cn/file.pptx
        if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(:\d+)?(\/|$)/i.test(s)) return 'https://' + s
        // qiniu object key (no host)
        if (s.charAt(0) === '/') return 'https://medias.kouyujie.cn' + s
        return 'https://medias.kouyujie.cn/' + s
      },
      guessType (name) {
        if (!name) return ''
        let lower = String(name).toLowerCase()
        let path = lower.split('?')[0].split('#')[0]
        if (path === 'text/csv') return 'csv'
        let ext = path.split('.').pop()
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].indexOf(ext) >= 0) return 'image'
        if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'].indexOf(ext) >= 0) return 'video'
        if (['mp3', 'wav', 'aac', 'ogg', 'm4a'].indexOf(ext) >= 0) return 'audio'
        if (ext === 'pdf') return 'pdf'
        if (['doc', 'docx', 'ppt', 'pptx'].indexOf(ext) >= 0) return 'office'
        if (['csv', 'xls', 'xlsx'].indexOf(ext) >= 0) return 'csv'
        return ext
      },
      loadCsvTable () {
        this.csvHeaders = []
        this.csvRows = []
        this.tableError = ''
        this.tableLoading = false
        if (this.resolvedType !== 'csv' || !this.httpsUrl) return
        let self = this
        let url = this.httpsUrl
        self.tableLoading = true
        self.$http.get(self.previewApi, {params: {url: url}}).then(function (res) {
          if (self.httpsUrl !== url) return
          self.tableLoading = false
          if (res && res.status === 'ok' && res.data) {
            self.csvHeaders = res.data.headers || []
            self.csvRows = res.data.rows || []
            if (!self.csvHeaders.length) self.tableError = 'Preview is empty.'
          } else {
            self.tableError = (res && res.msg) || 'Preview failed.'
          }
        }).catch(function () {
          if (self.httpsUrl !== url) return
          self.tableLoading = false
          self.tableError = 'Preview failed.'
        })
      },
      download () {
        if (this.httpsUrl) window.open(this.httpsUrl, '_blank')
      }
    }
  }
</script>
<style scoped>
  .file-preview-image { text-align: center; }
  .file-preview-image img { max-width: 100%; max-height: 600px; }
  .file-preview-media { width: 100%; max-height: 600px; display: block; }
  .file-preview-audio { width: 100%; }
  .handbook-content { width: 100%; min-height: 600px; background: #f5f5f5; border: 1px solid #dcdee2; border-radius: 4px; }
  .handbook-content iframe { display: block; }
  .csv-table-wrap { max-height: 600px; overflow: auto; border: 1px solid #dcdee2; border-radius: 4px; background: #fff; }
  .csv-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .csv-table th, .csv-table td { border: 1px solid #e8eaec; padding: 6px 8px; text-align: left; white-space: nowrap; }
  .csv-table th { background: #f8f8f9; font-weight: 600; }
  .file-preview-unknown { padding: 40px 0; text-align: center; color: #808695; }
</style>



// WEBPACK FOOTER //
// FilePreview.vue?bf5af0d4